import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const MONTHS = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Fev' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Abr' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Ago' },
  { value: 9, label: 'Set' },
  { value: 10, label: 'Out' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dez' },
];

const DreComparativa = () => {
  const anoAtual = Number(format(new Date(), 'yyyy'));
  const mesAtual = Number(format(new Date(), 'M'));
  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    ano_base: anoAtual,
    mes_base: mesAtual,
    regime: 'competencia',
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
  });
  const [dados, setDados] = useState({
    periodos: {
      atual: { label: '' },
      mes_anterior: { label: '' },
      ano_anterior: { label: '' },
    },
    totais: {
      receitas: { atual: 0, mes_anterior: 0, ano_anterior: 0 },
      despesas: { atual: 0, mes_anterior: 0, ano_anterior: 0 },
      resultado: {
        atual: 0,
        mes_anterior: 0,
        ano_anterior: 0,
        var_mm_abs: 0,
        var_mm_pct: null,
        var_aa_abs: 0,
        var_aa_pct: null,
      },
    },
    linhas: [],
  });

  const formatarValor = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatarPct = (value) => (value == null ? '-' : `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`);

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros da DRE comparativa:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/dre-comparativa', { params: filtros });
      const payload = response.data || {};
      setDados({
        periodos: payload.periodos || {
          atual: { label: '' },
          mes_anterior: { label: '' },
          ano_anterior: { label: '' },
        },
        totais: payload.totais || {
          receitas: { atual: 0, mes_anterior: 0, ano_anterior: 0 },
          despesas: { atual: 0, mes_anterior: 0, ano_anterior: 0 },
          resultado: {
            atual: 0,
            mes_anterior: 0,
            ano_anterior: 0,
            var_mm_abs: 0,
            var_mm_pct: null,
            var_aa_abs: 0,
            var_aa_pct: null,
          },
        },
        linhas: Array.isArray(payload.linhas) ? payload.linhas : [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar DRE comparativa');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const linhas = useMemo(
    () => (dados.linhas || []).filter((item) => Number(item.valor_atual || 0) !== 0 || Number(item.valor_mes_anterior || 0) !== 0 || Number(item.valor_ano_anterior || 0) !== 0),
    [dados.linhas]
  );

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DRE Comparativa</h1>
          <p className="text-gray-600">Comparativo M/M e A/A por conta da DRE</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="form-label">Ano Base</label>
              <input
                type="number"
                min="2000"
                max="2100"
                className="form-input"
                value={filtros.ano_base}
                onChange={(e) => setFiltros((old) => ({ ...old, ano_base: Number(e.target.value || anoAtual) }))}
              />
            </div>
            <div>
              <label className="form-label">Mes Base</label>
              <select
                className="form-select"
                value={filtros.mes_base}
                onChange={(e) => setFiltros((old) => ({ ...old, mes_base: Number(e.target.value) }))}
              >
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Regime</label>
              <select
                className="form-select"
                value={filtros.regime}
                onChange={(e) => setFiltros((old) => ({ ...old, regime: e.target.value }))}
              >
                <option value="competencia">Competencia</option>
                <option value="caixa">Caixa</option>
              </select>
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select
                className="form-select"
                value={filtros.centro_custo_id}
                onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}
              >
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Resultado {dados.periodos?.atual?.label || 'Atual'}</p>
            <p className={`text-2xl font-bold ${Number(dados.totais?.resultado?.atual || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.totais?.resultado?.atual)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Variação M/M</p>
            <p className={`text-2xl font-bold ${Number(dados.totais?.resultado?.var_mm_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.totais?.resultado?.var_mm_abs)}
            </p>
            <p className="text-xs text-gray-500">{formatarPct(dados.totais?.resultado?.var_mm_pct)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Variação A/A</p>
            <p className={`text-2xl font-bold ${Number(dados.totais?.resultado?.var_aa_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.totais?.resultado?.var_aa_abs)}
            </p>
            <p className="text-xs text-gray-500">{formatarPct(dados.totais?.resultado?.var_aa_pct)}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Comparativo por Conta</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>{dados.periodos?.atual?.label || 'Atual'}</th>
                  <th>{dados.periodos?.mes_anterior?.label || 'Mês Anterior'}</th>
                  <th>Var M/M (R$)</th>
                  <th>Var M/M (%)</th>
                  <th>{dados.periodos?.ano_anterior?.label || 'Ano Anterior'}</th>
                  <th>Var A/A (R$)</th>
                  <th>Var A/A (%)</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((linha) => (
                  <tr key={linha.plano_conta_id}>
                    <td>
                      <div className="max-w-xs">
                        <p className="font-medium text-gray-900">{linha.codigo} - {linha.descricao}</p>
                        <p className={`text-xs ${linha.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}`}>{linha.tipo} • {linha.dre_grupo || 'Sem grupo'}</p>
                      </div>
                    </td>
                    <td className={linha.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>R$ {formatarValor(linha.valor_atual)}</td>
                    <td className={linha.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>R$ {formatarValor(linha.valor_mes_anterior)}</td>
                    <td className={Number(linha.var_mm_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>R$ {formatarValor(linha.var_mm_abs)}</td>
                    <td className={Number(linha.var_mm_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>{formatarPct(linha.var_mm_pct)}</td>
                    <td className={linha.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>R$ {formatarValor(linha.valor_ano_anterior)}</td>
                    <td className={Number(linha.var_aa_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>R$ {formatarValor(linha.var_aa_abs)}</td>
                    <td className={Number(linha.var_aa_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>{formatarPct(linha.var_aa_pct)}</td>
                  </tr>
                ))}
                {linhas.length === 0 && !carregando && (
                  <tr>
                    <td colSpan={8} className="text-center text-gray-500 py-8">Nenhum dado encontrado para os filtros selecionados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DreComparativa;
